import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity,
  AlertTriangle,
  BarChart3,
  Boxes,
  Building2,
  CheckCircle2,
  Clock3,
  Database,
  DollarSign,
  LineChart,
  Package,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  TrendingDown,
  TrendingUp,
  Users,
} from "lucide-react";
import { fetchAdminDashboardSummary, AdminDashboardSummary } from "../../services/adminDashboardService";
import { listOrders, AdminOrder } from "../../services/adminOrdersService";
import { listUsers, AdminUser } from "../../services/adminService";
import { fetchProducts, Product } from "../../services/productService";
import {
  AadeGatewayCapabilities,
  AadeGatewayHealth,
  AnomalyAlert,
  Insight,
  PortfolioHealth,
  ProfitForecast,
  SyncJob,
} from "../../services/aadeService";
import { aadeGatewayService } from "../../services/aadeGatewayService";
import { aadeUiUtils } from "../../services/aadeUiUtils";

type TabKey =
  | "overview"
  | "commercial"
  | "operations"
  | "catalog"
  | "stakeholders"
  | "aade"
  | "governance";

type SourceState = "idle" | "loading" | "ready" | "error";

type SourceStatusMap = Record<
  "summary" | "orders" | "users" | "products" | "aade",
  { state: SourceState; error?: string; updatedAt?: string }
>;

const CURRENCY = new Intl.NumberFormat("en-NG", {
  style: "currency",
  currency: "NGN",
  maximumFractionDigits: 0,
});

const NUMBER = new Intl.NumberFormat("en-NG");

const tabs: Array<{ key: TabKey; label: string; icon: React.ComponentType<{ className?: string }> }> = [
  { key: "overview", label: "Executive", icon: BarChart3 },
  { key: "commercial", label: "Commercial", icon: DollarSign },
  { key: "operations", label: "Operations", icon: Activity },
  { key: "catalog", label: "Catalog", icon: Boxes },
  { key: "stakeholders", label: "Stakeholders", icon: Users },
  { key: "aade", label: "AADE Intelligence", icon: Sparkles },
  { key: "governance", label: "Governance", icon: Database },
];

const statusOrder = ["pending", "processing", "shipped", "delivered", "cancelled"];
const analyticsSourceKeys: Array<keyof SourceStatusMap> = ["summary", "orders", "users", "products", "aade"];

const getCookieValue = (name: string): string | null => {
  if (typeof document === "undefined") return null;
  const cookies = document.cookie ? document.cookie.split("; ") : [];
  for (const cookie of cookies) {
    const [key, ...rest] = cookie.split("=");
    if (key === name) return decodeURIComponent(rest.join("="));
  }
  return null;
};

const safeParse = <T,>(value: string | null): T | null => {
  if (!value) return null;
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
};

const resolveTenantContext = () => {
  const tenant = safeParse<{ id?: string }>(localStorage.getItem("currentTenant"));
  const userData =
    safeParse<{ tenant_id?: string; email?: string; full_name?: string }>(localStorage.getItem("user_data")) ||
    safeParse<{ tenant_id?: string; email?: string; full_name?: string }>(getCookieValue("user_data"));
  const user = safeParse<{ tenant_id?: string; email?: string }>(localStorage.getItem("user"));
  const tenantId = tenant?.id || userData?.tenant_id || user?.tenant_id || "";
  return {
    tenantId,
    userEmail: userData?.email || user?.email || "Unknown user",
    userName: userData?.full_name || userData?.email || user?.email || "Unknown user",
    hasTenant: Boolean(tenantId),
  };
};

const formatCurrency = (value: number) => CURRENCY.format(Number.isFinite(value) ? value : 0);
const formatNumber = (value: number) => NUMBER.format(Number.isFinite(value) ? value : 0);

const formatDateShort = (date: Date) =>
  date.toLocaleDateString("en-NG", { month: "short", day: "numeric" });

const formatDateTime = (iso?: string | null) => {
  if (!iso) return "N/A";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "N/A";
  return d.toLocaleString();
};

const dayKey = (date: Date) => date.toISOString().slice(0, 10);

const parseDate = (value?: string | null) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

const buildRecentDays = (days: number) => {
  const today = startOfDay(new Date());
  return Array.from({ length: days }, (_, index) => {
    const d = new Date(today);
    d.setDate(today.getDate() - (days - 1 - index));
    return d;
  });
};

const clampPercent = (n: number) => Math.max(0, Math.min(100, n));

const sourceBadgeClasses = (source: "AgriFlow CRUD" | "AADE") =>
  source === "AADE"
    ? "bg-indigo-100 text-indigo-700 border-indigo-200"
    : "bg-emerald-100 text-emerald-700 border-emerald-200";

const Panel: React.FC<{ title: string; subtitle?: string; right?: React.ReactNode; children: React.ReactNode }> = ({
  title,
  subtitle,
  right,
  children,
}) => (
  <section className="rounded-2xl border border-gray-200 bg-white shadow-sm">
    <div className="flex flex-col gap-3 border-b border-gray-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        {subtitle && <p className="mt-1 text-sm text-gray-500">{subtitle}</p>}
      </div>
      {right}
    </div>
    <div className="p-5">{children}</div>
  </section>
);

const KPI: React.FC<{
  label: string;
  value: string;
  helper?: string;
  tone?: "neutral" | "good" | "warn" | "danger";
  icon?: React.ReactNode;
}> = ({ label, value, helper, tone = "neutral", icon }) => {
  const toneClasses =
    tone === "good"
      ? "border-emerald-200 bg-emerald-50"
      : tone === "warn"
      ? "border-amber-200 bg-amber-50"
      : tone === "danger"
      ? "border-rose-200 bg-rose-50"
      : "border-gray-200 bg-white";
  return (
    <div className={`rounded-xl border p-4 ${toneClasses}`}>
      <div className="mb-2 flex items-center justify-between gap-2 text-xs font-medium uppercase tracking-wide text-gray-600">
        <span>{label}</span>
        {icon}
      </div>
      <div className="text-2xl font-bold text-gray-900">{value}</div>
      {helper && <div className="mt-1 text-xs text-gray-600">{helper}</div>}
    </div>
  );
};

const ProgressList: React.FC<{
  rows: Array<{ label: string; value: number; display?: string; tone?: string }>;
  valueFormatter?: (value: number) => string;
}> = ({ rows, valueFormatter }) => {
  const max = Math.max(...rows.map((r) => r.value), 1);
  return (
    <div className="space-y-3">
      {rows.map((row) => (
        <div key={row.label}>
          <div className="mb-1 flex items-center justify-between gap-3 text-sm">
            <span className="text-gray-700">{row.label}</span>
            <span className="font-semibold text-gray-900">
              {row.display ?? (valueFormatter ? valueFormatter(row.value) : formatNumber(row.value))}
            </span>
          </div>
          <div className="h-2 rounded-full bg-gray-100">
            <div
              className={`h-2 rounded-full ${row.tone ?? "bg-green-600"}`}
              style={{ width: `${clampPercent((row.value / max) * 100)}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

const MiniBarChart: React.FC<{
  rows: Array<{ label: string; value: number; color?: string; secondary?: string }>;
  formatter?: (value: number) => string;
  height?: number;
}> = ({ rows, formatter = formatNumber, height = 140 }) => {
  const max = Math.max(...rows.map((r) => r.value), 1);
  return (
    <div>
      <div className="flex items-end gap-2" style={{ height }}>
        {rows.map((row) => (
          <div key={row.label} className="flex min-w-0 flex-1 flex-col items-center gap-2">
            <div className="text-[10px] font-medium text-gray-500">{formatter(row.value)}</div>
            <div className="relative flex h-full w-full items-end rounded-md bg-gray-50 px-1">
              <div
                className={`w-full rounded-t-md ${row.color ?? "bg-green-600"}`}
                style={{ height: `${Math.max(6, (row.value / max) * 100)}%` }}
                title={`${row.label}: ${formatter(row.value)}`}
              />
            </div>
            <div className="truncate text-[10px] text-gray-600" title={row.label}>
              {row.label}
            </div>
            {row.secondary && <div className="text-[10px] text-gray-400">{row.secondary}</div>}
          </div>
        ))}
      </div>
    </div>
  );
};

const DataTable: React.FC<{
  columns: string[];
  rows: Array<Array<React.ReactNode>>;
  emptyMessage?: string;
}> = ({ columns, rows, emptyMessage = "No data available" }) => (
  <div className="overflow-x-auto">
    <table className="min-w-full text-sm">
      <thead>
        <tr className="border-b border-gray-200">
          {columns.map((col) => (
            <th key={col} className="px-3 py-2 text-left font-semibold text-gray-700">
              {col}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr>
            <td className="px-3 py-6 text-gray-500" colSpan={columns.length}>
              {emptyMessage}
            </td>
          </tr>
        ) : (
          rows.map((row, index) => (
            <tr key={index} className="border-b border-gray-100 last:border-0">
              {row.map((cell, i) => (
                <td key={i} className="px-3 py-2 align-top text-gray-800">
                  {cell}
                </td>
              ))}
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

const statusTone = (status: string) => {
  const s = status.toLowerCase();
  if (s === "delivered" || s === "completed") return "bg-emerald-100 text-emerald-700";
  if (s === "processing" || s === "shipped") return "bg-blue-100 text-blue-700";
  if (s === "pending") return "bg-amber-100 text-amber-700";
  if (s === "cancelled" || s === "failed") return "bg-rose-100 text-rose-700";
  return "bg-gray-100 text-gray-700";
};

const AdminAnalytics: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabKey>("overview");
  const [timeRangeDays, setTimeRangeDays] = useState<7 | 30 | 90>(30);
  const [loading, setLoading] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);
  const [lastRefreshAt, setLastRefreshAt] = useState<string | null>(null);

  const [summary, setSummary] = useState<AdminDashboardSummary | null>(null);
  const [orders, setOrders] = useState<AdminOrder[]>([]);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [aadePortfolio, setAadePortfolio] = useState<PortfolioHealth | null>(null);
  const [aadeAnomalies, setAadeAnomalies] = useState<AnomalyAlert[]>([]);
  const [aadeInsights, setAadeInsights] = useState<Insight[]>([]);
  const [aadeSyncJobs, setAadeSyncJobs] = useState<SyncJob[]>([]);
  const [aadeForecast, setAadeForecast] = useState<ProfitForecast | null>(null);
  const [aadeUploadId, setAadeUploadId] = useState<string | null>(null);
  const [aadeGatewayHealth, setAadeGatewayHealth] = useState<AadeGatewayHealth | null>(null);
  const [aadeGatewayCapabilities, setAadeGatewayCapabilities] = useState<AadeGatewayCapabilities | null>(null);

  const [sourceStatus, setSourceStatus] = useState<SourceStatusMap>({
    summary: { state: "idle" },
    orders: { state: "idle" },
    users: { state: "idle" },
    products: { state: "idle" },
    aade: { state: "idle" },
  });

  const tenantContext = useMemo(() => resolveTenantContext(), [lastRefreshAt]);

  const updateSource = useCallback(
    (
      key: keyof SourceStatusMap,
      patch: Partial<{ state: SourceState; error?: string; updatedAt?: string }>
    ) => {
      setSourceStatus((prev) => ({
        ...prev,
        [key]: {
          ...prev[key],
          ...patch,
        },
      }));
    },
    []
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    setPageError(null);
    const currentTenant = resolveTenantContext();

    analyticsSourceKeys.forEach((key) => {
      updateSource(key, { state: "loading", error: undefined });
    });

    const stamp = new Date().toISOString();

    const tasks = [
      (async () => {
        try {
          const data = await fetchAdminDashboardSummary();
          setSummary(data);
          updateSource("summary", { state: "ready", updatedAt: stamp });
        } catch (err: any) {
          updateSource("summary", {
            state: "error",
            error: err?.response?.data?.detail || err?.message || "Failed to load admin summary",
          });
        }
      })(),
      (async () => {
        try {
          const data = await listOrders();
          setOrders(Array.isArray(data) ? data : []);
          updateSource("orders", { state: "ready", updatedAt: stamp });
        } catch (err: any) {
          updateSource("orders", {
            state: "error",
            error: err?.response?.data?.detail || err?.message || "Failed to load orders",
          });
        }
      })(),
      (async () => {
        try {
          const data = await listUsers();
          setUsers(Array.isArray(data) ? data : []);
          updateSource("users", { state: "ready", updatedAt: stamp });
        } catch (err: any) {
          updateSource("users", {
            state: "error",
            error: err?.response?.data?.detail || err?.message || "Failed to load users",
          });
        }
      })(),
      (async () => {
        try {
          const tenantId = currentTenant.tenantId || "";
          const data = await fetchProducts(tenantId, { limit: 500, sort_by: "created_at", sort_order: "desc" });
          setProducts(Array.isArray(data) ? data : []);
          updateSource("products", { state: "ready", updatedAt: stamp });
        } catch (err: any) {
          updateSource("products", {
            state: "error",
            error: err?.response?.data?.detail || err?.message || "Failed to load products",
          });
        }
      })(),
      (async () => {
        try {
          const [gatewayHealth, gatewayCaps, portfolio, anomalies, sync, insights, forecast] = await Promise.allSettled([
            aadeGatewayService.getHealth(),
            aadeGatewayService.getCapabilities(),
            aadeGatewayService.getPortfolioHealth(),
            aadeGatewayService.getAnomalyAlerts(72),
            aadeGatewayService.getSyncStatus(10),
            aadeGatewayService.getLatestInsights(undefined, 12),
            aadeGatewayService.getProfitForecast(),
          ]);

          if (gatewayHealth.status === "fulfilled") setAadeGatewayHealth(gatewayHealth.value);
          if (gatewayCaps.status === "fulfilled") setAadeGatewayCapabilities(gatewayCaps.value);
          if (portfolio.status === "fulfilled") setAadePortfolio(portfolio.value);
          if (anomalies.status === "fulfilled") setAadeAnomalies(anomalies.value.alerts ?? []);
          if (sync.status === "fulfilled") setAadeSyncJobs(sync.value.sync_jobs ?? []);
          if (insights.status === "fulfilled") {
            setAadeInsights(insights.value.insights ?? []);
            setAadeUploadId(insights.value.upload_id ?? null);
          }
          if (forecast.status === "fulfilled") setAadeForecast(forecast.value);

          const rejected = [gatewayHealth, gatewayCaps, portfolio, anomalies, sync, insights, forecast].filter((r) => r.status === "rejected");
          if (rejected.length === 5) {
            throw new Error("AADE endpoints unavailable");
          }
          updateSource("aade", { state: "ready", updatedAt: stamp });
        } catch (err: any) {
          updateSource("aade", {
            state: "error",
            error: err?.response?.data?.detail || err?.message || "Failed to load AADE analytics",
          });
        }
      })(),
    ];

    await Promise.allSettled(tasks);

    setLastRefreshAt(stamp);
    setLoading(false);
  }, [updateSource]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const productsById = useMemo(
    () => Object.fromEntries(products.map((product) => [product.id, product])),
    [products]
  );

  const datedOrders = useMemo(
    () =>
      orders
        .map((order) => ({ ...order, parsedDate: parseDate(order.created_at) }))
        .filter((order): order is AdminOrder & { parsedDate: Date } => Boolean(order.parsedDate)),
    [orders]
  );

  const recentWindowOrders = useMemo(() => {
    const now = new Date();
    const start = new Date(now);
    start.setDate(now.getDate() - timeRangeDays + 1);
    return datedOrders.filter((order) => order.parsedDate >= start);
  }, [datedOrders, timeRangeDays]);

  const recentDays = useMemo(() => buildRecentDays(Math.min(timeRangeDays, 30)), [timeRangeDays]);

  const dailySeries = useMemo(() => {
    const buckets = new Map<string, { revenue: number; orders: number }>();
    recentDays.forEach((d) => buckets.set(dayKey(d), { revenue: 0, orders: 0 }));

    recentWindowOrders.forEach((order) => {
      const key = dayKey(order.parsedDate);
      if (!buckets.has(key)) return;
      const current = buckets.get(key)!;
      current.orders += 1;
      current.revenue += Number(order.total_price || 0);
    });

    return recentDays.map((d) => {
      const key = dayKey(d);
      const bucket = buckets.get(key)!;
      return {
        date: d,
        label: formatDateShort(d),
        revenue: bucket.revenue,
        orders: bucket.orders,
      };
    });
  }, [recentDays, recentWindowOrders]);

  const statusBreakdown = useMemo(() => {
    const acc: Record<string, { count: number; revenue: number }> = {};
    orders.forEach((order) => {
      const status = (order.status || "unknown").toLowerCase();
      if (!acc[status]) acc[status] = { count: 0, revenue: 0 };
      acc[status].count += 1;
      acc[status].revenue += Number(order.total_price || 0);
    });
    return Object.entries(acc)
      .sort((a, b) => {
        const ai = statusOrder.indexOf(a[0]);
        const bi = statusOrder.indexOf(b[0]);
        if (ai === -1 && bi === -1) return b[1].count - a[1].count;
        if (ai === -1) return 1;
        if (bi === -1) return -1;
        return ai - bi;
      })
      .map(([status, data]) => ({ status, ...data }));
  }, [orders]);

  const orderTotals = useMemo(() => {
    const totalRevenue = orders.reduce((sum, o) => sum + Number(o.total_price || 0), 0);
    const totalQuantity = orders.reduce((sum, o) => sum + Number(o.quantity || 0), 0);
    const pendingCount = orders.filter((o) => (o.status || "").toLowerCase() === "pending").length;
    const deliveredCount = orders.filter((o) => (o.status || "").toLowerCase() === "delivered").length;
    const cancellationCount = orders.filter((o) =>
      ["cancelled", "failed"].includes((o.status || "").toLowerCase())
    ).length;
    const averageOrderValue = orders.length ? totalRevenue / orders.length : 0;
    const averageQuantity = orders.length ? totalQuantity / orders.length : 0;
    return {
      totalRevenue,
      totalQuantity,
      pendingCount,
      deliveredCount,
      cancellationCount,
      averageOrderValue,
      averageQuantity,
    };
  }, [orders]);

  const topProductsByRevenue = useMemo(() => {
    const map = new Map<
      string,
      { productId: string; name: string; orderCount: number; quantity: number; revenue: number; stock?: number; category?: string }
    >();
    orders.forEach((order) => {
      const product = productsById[order.product_id];
      const key = order.product_id || "unknown";
      const current = map.get(key) || {
        productId: key,
        name: product?.name || `Product ${key.slice(0, 8)}`,
        orderCount: 0,
        quantity: 0,
        revenue: 0,
        stock: product?.current_stock,
        category: product?.category,
      };
      current.orderCount += 1;
      current.quantity += Number(order.quantity || 0);
      current.revenue += Number(order.total_price || 0);
      current.stock = product?.current_stock;
      current.category = product?.category;
      map.set(key, current);
    });
    return Array.from(map.values()).sort((a, b) => b.revenue - a.revenue);
  }, [orders, productsById]);

  const inventoryStats = useMemo(() => {
    const totalProducts = products.length;
    const activeProducts = products.filter((p) => p.is_available).length;
    const featuredProducts = products.filter((p) => p.is_featured).length;
    const totalStockUnits = products.reduce((sum, p) => sum + Number(p.current_stock || 0), 0);
    const lowStockThreshold = 20;
    const lowStockCount = products.filter((p) => Number(p.current_stock || 0) <= lowStockThreshold).length;

    const byCategory = new Map<string, { category: string; products: number; stock: number; active: number; avgPrice: number }>();
    products.forEach((product) => {
      const category = product.category || "Uncategorized";
      const current = byCategory.get(category) || {
        category,
        products: 0,
        stock: 0,
        active: 0,
        avgPrice: 0,
      };
      current.products += 1;
      current.stock += Number(product.current_stock || 0);
      current.active += product.is_available ? 1 : 0;
      current.avgPrice += Number(product.price_per_unit || 0);
      byCategory.set(category, current);
    });
    const categoryRows = Array.from(byCategory.values())
      .map((row) => ({
        ...row,
        avgPrice: row.products ? row.avgPrice / row.products : 0,
      }))
      .sort((a, b) => b.products - a.products);

    return {
      totalProducts,
      activeProducts,
      featuredProducts,
      totalStockUnits,
      lowStockCount,
      categoryRows,
    };
  }, [products]);

  const stakeholderBreakdown = useMemo(() => {
    const roleMap = new Map<string, { role: string; count: number; active: number; inactive: number }>();
    users.forEach((user) => {
      const role = (user.role || "unknown").toLowerCase();
      const current = roleMap.get(role) || { role, count: 0, active: 0, inactive: 0 };
      current.count += 1;
      if (user.is_active === false) current.inactive += 1;
      else current.active += 1;
      roleMap.set(role, current);
    });
    return Array.from(roleMap.values()).sort((a, b) => b.count - a.count);
  }, [users]);

  const customerOrderStats = useMemo(() => {
    const map = new Map<string, { customerId: string; orders: number; revenue: number; lastOrderAt?: Date }>();
    datedOrders.forEach((order) => {
      const key = order.customer_id || "unknown";
      const current = map.get(key) || { customerId: key, orders: 0, revenue: 0 };
      current.orders += 1;
      current.revenue += Number(order.total_price || 0);
      if (!current.lastOrderAt || order.parsedDate > current.lastOrderAt) current.lastOrderAt = order.parsedDate;
      map.set(key, current);
    });
    const rows = Array.from(map.values()).sort((a, b) => b.revenue - a.revenue);
    const repeatCustomers = rows.filter((r) => r.orders > 1).length;
    return {
      rows,
      repeatCustomers,
      singleOrderCustomers: rows.filter((r) => r.orders === 1).length,
      totalCustomersWithOrders: rows.length,
    };
  }, [datedOrders]);

  const backlogAging = useMemo(() => {
    const openStatuses = new Set(["pending", "processing", "shipped"]);
    const buckets = {
      "0-1d": 0,
      "2-3d": 0,
      "4-7d": 0,
      "8d+": 0,
    };
    const now = Date.now();
    datedOrders.forEach((order) => {
      const status = (order.status || "").toLowerCase();
      if (!openStatuses.has(status)) return;
      const ageDays = (now - order.parsedDate.getTime()) / (1000 * 60 * 60 * 24);
      if (ageDays <= 1) buckets["0-1d"] += 1;
      else if (ageDays <= 3) buckets["2-3d"] += 1;
      else if (ageDays <= 7) buckets["4-7d"] += 1;
      else buckets["8d+"] += 1;
    });
    return buckets;
  }, [datedOrders]);

  const weeklyRollup = useMemo(() => {
    const map = new Map<string, { orders: number; revenue: number }>();
    recentWindowOrders.forEach((order) => {
      const d = new Date(order.parsedDate);
      const start = new Date(d);
      const day = start.getDay();
      const offset = (day + 6) % 7;
      start.setDate(start.getDate() - offset);
      start.setHours(0, 0, 0, 0);
      const key = dayKey(start);
      const current = map.get(key) || { orders: 0, revenue: 0 };
      current.orders += 1;
      current.revenue += Number(order.total_price || 0);
      map.set(key, current);
    });
    return Array.from(map.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .slice(-8)
      .map(([weekStart, v]) => ({
        weekStart,
        ...v,
      }));
  }, [recentWindowOrders]);

  const totalDataPoints = orders.length + users.length + products.length + (summary?.recentOrders.length || 0);

  const summaryStats = summary?.stats;

  const topCategoriesByStock = inventoryStats.categoryRows.slice(0, 8).map((row) => ({
    label: row.category,
    value: row.stock,
    display: formatNumber(row.stock),
    tone: "bg-green-600",
  }));

  const topCategoriesByProducts = inventoryStats.categoryRows.slice(0, 8).map((row) => ({
    label: row.category,
    value: row.products,
    display: formatNumber(row.products),
    tone: "bg-emerald-500",
  }));

  const openSourceErrors = Object.entries(sourceStatus).filter(([, s]) => s.state === "error");

  const aadePriorityBreakdown = useMemo(() => {
    const counts: Record<string, number> = {};
    aadeInsights.forEach((insight) => {
      const key = insight.priority || "unknown";
      counts[key] = (counts[key] || 0) + 1;
    });
    return Object.entries(counts).map(([label, value]) => ({ label, value }));
  }, [aadeInsights]);

  const governanceRows = [
    {
      source: "AgriFlow CRUD analytics",
      owner: "AgriFlow Server",
      scope: "Tenant scoped via X-Tenant-ID",
      coverage: "Products, Orders, Users, Admin Summary",
      freshness: sourceStatus.summary.updatedAt || sourceStatus.orders.updatedAt || sourceStatus.products.updatedAt || "N/A",
      state: [sourceStatus.summary, sourceStatus.orders, sourceStatus.products, sourceStatus.users].some((s) => s.state === "error")
        ? "Partial"
        : "Healthy",
    },
    {
      source: "AADE advanced analytics",
      owner: "AADE (Assistant Analyst Data Engine)",
      scope: "Tenant scoped via auth + tenant header",
      coverage: "Portfolio health, anomalies, insights, forecast, sync jobs",
      freshness: sourceStatus.aade.updatedAt || "N/A",
      state: sourceStatus.aade.state === "ready" ? "Healthy" : sourceStatus.aade.state === "error" ? "Unavailable" : "Loading",
    },
  ];

  const renderOverview = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <KPI
          label="Revenue (All Orders)"
          value={formatCurrency(orderTotals.totalRevenue || summaryStats?.monthlyRevenue || 0)}
          helper="Tenant-scoped aggregate from orders endpoint"
          icon={<DollarSign className="h-4 w-4 text-gray-500" />}
          tone="good"
        />
        <KPI
          label="Orders"
          value={formatNumber(orders.length)}
          helper={`${formatNumber(orderTotals.deliveredCount)} delivered • ${formatNumber(orderTotals.pendingCount)} pending`}
          icon={<Package className="h-4 w-4 text-gray-500" />}
        />
        <KPI
          label="Customers"
          value={formatNumber(summaryStats?.totalCustomers || stakeholderBreakdown.find((r) => r.role === "customer")?.count || 0)}
          helper={`${formatNumber(customerOrderStats.repeatCustomers)} repeat buyers`}
          icon={<Users className="h-4 w-4 text-gray-500" />}
        />
        <KPI
          label="Catalog Health"
          value={`${formatNumber(inventoryStats.activeProducts)}/${formatNumber(inventoryStats.totalProducts)}`}
          helper={`${formatNumber(inventoryStats.lowStockCount || summaryStats?.lowStockItems || 0)} low stock SKUs`}
          icon={<ShieldCheck className="h-4 w-4 text-gray-500" />}
          tone={inventoryStats.lowStockCount > 0 ? "warn" : "good"}
        />
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className="xl:col-span-2">
          <Panel
            title="Revenue & Order Trend"
            subtitle={`Last ${Math.min(timeRangeDays, 30)} days (visualized daily)`}
            right={<span className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">AgriFlow CRUD</span>}
          >
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
              <div>
                <div className="mb-2 text-sm font-medium text-gray-700">Daily Revenue</div>
                <MiniBarChart
                  rows={dailySeries.map((d) => ({ label: d.label, value: d.revenue, color: "bg-green-600" }))}
                  formatter={formatCurrency}
                />
              </div>
              <div>
                <div className="mb-2 text-sm font-medium text-gray-700">Daily Orders</div>
                <MiniBarChart
                  rows={dailySeries.map((d) => ({ label: d.label, value: d.orders, color: "bg-blue-600" }))}
                  formatter={formatNumber}
                />
              </div>
            </div>
          </Panel>
        </div>

        <Panel title="Order Status Mix" subtitle="Pipeline health by current status">
          <ProgressList
            rows={statusBreakdown.map((row) => ({
              label: row.status,
              value: row.count,
              display: `${formatNumber(row.count)} (${formatCurrency(row.revenue)})`,
              tone:
                row.status === "delivered"
                  ? "bg-emerald-600"
                  : row.status === "pending"
                  ? "bg-amber-500"
                  : row.status === "processing" || row.status === "shipped"
                  ? "bg-blue-600"
                  : "bg-rose-500",
            }))}
          />
        </Panel>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <Panel title="Top Revenue Products" subtitle="Cross-reference orders with product catalog">
          <DataTable
            columns={["Product", "Category", "Orders", "Qty", "Revenue", "Stock"]}
            rows={topProductsByRevenue.slice(0, 8).map((item) => [
              <div className="font-medium text-gray-900">{item.name}</div>,
              item.category || "N/A",
              formatNumber(item.orderCount),
              formatNumber(item.quantity),
              formatCurrency(item.revenue),
              item.stock != null ? formatNumber(item.stock) : "N/A",
            ])}
          />
        </Panel>

        <Panel title="Recent Orders Requiring Attention" subtitle="Admin summary feed + operational status">
          <DataTable
            columns={["Order", "Customer", "Items", "Amount", "Status", "Date"]}
            rows={(summary?.recentOrders || []).slice(0, 8).map((order) => [
              <span className="font-mono text-xs">{order.id.slice(0, 8)}</span>,
              order.customerName || "Unknown",
              order.items || "N/A",
              formatCurrency(order.amount || 0),
              <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusTone(order.status)}`}>
                {order.status}
              </span>,
              formatDateTime(order.date),
            ])}
            emptyMessage="No recent orders in admin summary."
          />
        </Panel>
      </div>
    </div>
  );

  const renderCommercial = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
        <KPI label="AOV" value={formatCurrency(orderTotals.averageOrderValue)} helper="Average order value" icon={<LineChart className="h-4 w-4 text-gray-500" />} />
        <KPI label="Avg Qty / Order" value={formatNumber(orderTotals.averageQuantity)} helper="Quantity units per order" />
        <KPI label="Repeat Buyers" value={formatNumber(customerOrderStats.repeatCustomers)} helper="Customers with 2+ orders" tone="good" icon={<TrendingUp className="h-4 w-4 text-gray-500" />} />
        <KPI label="One-Time Buyers" value={formatNumber(customerOrderStats.singleOrderCustomers)} helper="Single-order customers" tone="warn" icon={<TrendingDown className="h-4 w-4 text-gray-500" />} />
        <KPI
          label="Repeat Ratio"
          value={`${customerOrderStats.totalCustomersWithOrders ? ((customerOrderStats.repeatCustomers / customerOrderStats.totalCustomersWithOrders) * 100).toFixed(1) : "0.0"}%`}
          helper="Repeat customer share"
        />
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <Panel title="Weekly Commercial Performance" subtitle={`Rolled up from order transactions (${timeRangeDays}d window)`}>
          <DataTable
            columns={["Week Start", "Orders", "Revenue", "AOV"]}
            rows={weeklyRollup.map((w) => [
              formatDateTime(w.weekStart),
              formatNumber(w.orders),
              formatCurrency(w.revenue),
              formatCurrency(w.orders ? w.revenue / w.orders : 0),
            ])}
            emptyMessage="Not enough dated orders to build weekly rollups."
          />
        </Panel>

        <Panel title="Top Customers by Revenue" subtitle="Stakeholder prioritization for sales/relationship teams">
          <DataTable
            columns={["Customer ID", "Orders", "Revenue", "Last Order", "Tier"]}
            rows={customerOrderStats.rows.slice(0, 10).map((customer) => {
              const tier =
                customer.revenue >= 1_000_000 ? "Strategic" : customer.revenue >= 250_000 ? "Growth" : "Standard";
              return [
                <span className="font-mono text-xs">{customer.customerId.slice(0, 12)}</span>,
                formatNumber(customer.orders),
                formatCurrency(customer.revenue),
                customer.lastOrderAt ? customer.lastOrderAt.toLocaleDateString() : "N/A",
                <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">{tier}</span>,
              ];
            })}
          />
        </Panel>
      </div>

      <Panel title="Revenue Concentration" subtitle="Product contribution by revenue (top 10 products)">
        <ProgressList
          rows={topProductsByRevenue.slice(0, 10).map((p) => ({
            label: p.name,
            value: p.revenue,
            display: `${formatCurrency(p.revenue)} • ${formatNumber(p.orderCount)} orders`,
            tone: "bg-green-600",
          }))}
          valueFormatter={(v) => formatCurrency(v)}
        />
      </Panel>
    </div>
  );

  const renderOperations = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <KPI label="Pending Queue" value={formatNumber(orderTotals.pendingCount)} helper="Open orders awaiting handling" tone={orderTotals.pendingCount > 20 ? "warn" : "neutral"} icon={<Clock3 className="h-4 w-4 text-gray-500" />} />
        <KPI label="Delivered Orders" value={formatNumber(orderTotals.deliveredCount)} helper="Closed / completed delivery status" tone="good" icon={<CheckCircle2 className="h-4 w-4 text-gray-500" />} />
        <KPI label="Cancellation / Failure" value={formatNumber(orderTotals.cancellationCount)} helper="Cancelled + failed statuses" tone={orderTotals.cancellationCount > 0 ? "warn" : "neutral"} icon={<AlertTriangle className="h-4 w-4 text-gray-500" />} />
        <KPI
          label="Open Backlog"
          value={formatNumber(
            statusBreakdown
              .filter((s) => ["pending", "processing", "shipped"].includes(s.status))
              .reduce((sum, s) => sum + s.count, 0)
          )}
          helper="Orders still in fulfillment pipeline"
          icon={<Activity className="h-4 w-4 text-gray-500" />}
        />
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <Panel title="Backlog Aging (Open Orders)" subtitle="Operational risk buckets based on order age">
          <ProgressList
            rows={Object.entries(backlogAging).map(([label, value]) => ({
              label,
              value,
              tone: label === "8d+" ? "bg-rose-500" : label === "4-7d" ? "bg-amber-500" : "bg-blue-600",
            }))}
          />
        </Panel>

        <Panel title="Fulfillment Pipeline" subtitle="Count and value by status for ops review">
          <DataTable
            columns={["Status", "Orders", "Revenue", "Share"]}
            rows={statusBreakdown.map((row) => [
              <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusTone(row.status)}`}>
                {row.status}
              </span>,
              formatNumber(row.count),
              formatCurrency(row.revenue),
              `${orders.length ? ((row.count / orders.length) * 100).toFixed(1) : "0.0"}%`,
            ])}
          />
        </Panel>
      </div>

      <Panel title="Operational Exception Queue" subtitle="Low-stock and high-demand products likely to impact fulfillment">
        <DataTable
          columns={["Product", "Stock", "Orders", "Revenue", "Risk", "Action"]}
          rows={topProductsByRevenue
            .filter((p) => (p.stock ?? 999999) <= 20 || p.orderCount >= 5)
            .slice(0, 12)
            .map((p) => {
              const lowStock = (p.stock ?? 999999) <= 20;
              const risk = lowStock && p.orderCount >= 5 ? "High" : lowStock ? "Medium" : "Demand Watch";
              const action = lowStock ? "Replenish / supplier follow-up" : "Monitor demand spike";
              return [
                p.name,
                p.stock != null ? formatNumber(p.stock) : "N/A",
                formatNumber(p.orderCount),
                formatCurrency(p.revenue),
                <span
                  className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                    risk === "High"
                      ? "bg-rose-100 text-rose-700"
                      : risk === "Medium"
                      ? "bg-amber-100 text-amber-700"
                      : "bg-blue-100 text-blue-700"
                  }`}
                >
                  {risk}
                </span>,
                action,
              ];
            })}
        />
      </Panel>
    </div>
  );

  const renderCatalog = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
        <KPI label="Total SKUs" value={formatNumber(inventoryStats.totalProducts || summaryStats?.totalProducts || 0)} helper="Tenant catalog size" icon={<Boxes className="h-4 w-4 text-gray-500" />} />
        <KPI label="Active SKUs" value={formatNumber(inventoryStats.activeProducts)} helper="Available for ordering" tone="good" icon={<CheckCircle2 className="h-4 w-4 text-gray-500" />} />
        <KPI label="Featured SKUs" value={formatNumber(inventoryStats.featuredProducts)} helper="Merchandising highlights" icon={<Sparkles className="h-4 w-4 text-gray-500" />} />
        <KPI label="Total Stock Units" value={formatNumber(inventoryStats.totalStockUnits)} helper="Current stock sum across products" />
        <KPI
          label="Low Stock SKUs"
          value={formatNumber(inventoryStats.lowStockCount || summaryStats?.lowStockItems || 0)}
          helper="<= 20 stock threshold"
          tone={(inventoryStats.lowStockCount || 0) > 0 ? "warn" : "good"}
          icon={<AlertTriangle className="h-4 w-4 text-gray-500" />}
        />
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <Panel title="Category Distribution (SKU Count)" subtitle="Catalog breadth by category">
          <ProgressList rows={topCategoriesByProducts} />
        </Panel>
        <Panel title="Category Distribution (Stock Units)" subtitle="Inventory depth by category">
          <ProgressList rows={topCategoriesByStock} />
        </Panel>
      </div>

      <Panel title="Catalog Category Table" subtitle="Detailed category-level inventory and pricing snapshot">
        <DataTable
          columns={["Category", "SKUs", "Active", "Availability Rate", "Total Stock", "Avg Price"]}
          rows={inventoryStats.categoryRows.map((row) => [
            row.category,
            formatNumber(row.products),
            formatNumber(row.active),
            `${row.products ? ((row.active / row.products) * 100).toFixed(1) : "0.0"}%`,
            formatNumber(row.stock),
            formatCurrency(row.avgPrice),
          ])}
        />
      </Panel>

      <Panel title="Low Stock Watchlist" subtitle="Actionable list for procurement and replenishment teams">
        <DataTable
          columns={["Product", "Category", "Stock", "Price", "Availability", "Demand Signal"]}
          rows={products
            .filter((p) => Number(p.current_stock || 0) <= 20)
            .sort((a, b) => Number(a.current_stock || 0) - Number(b.current_stock || 0))
            .slice(0, 20)
            .map((p) => {
              const demandSignal = topProductsByRevenue.find((t) => t.productId === p.id);
              return [
                <span className="font-medium">{p.name}</span>,
                p.category || "N/A",
                formatNumber(p.current_stock || 0),
                formatCurrency(p.price_per_unit || 0),
                p.is_available ? (
                  <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">Active</span>
                ) : (
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">Inactive</span>
                ),
                demandSignal ? `${formatNumber(demandSignal.orderCount)} orders / ${formatCurrency(demandSignal.revenue)}` : "No recent orders",
              ];
            })}
        />
      </Panel>
    </div>
  );

  const renderStakeholders = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <KPI label="Users (All Roles)" value={formatNumber(users.length)} helper="Tenant-visible admin/users endpoint" icon={<Users className="h-4 w-4 text-gray-500" />} />
        <KPI label="Roles Tracked" value={formatNumber(stakeholderBreakdown.length)} helper="Distinct roles in user dataset" icon={<Building2 className="h-4 w-4 text-gray-500" />} />
        <KPI
          label="Active Users"
          value={formatNumber(users.filter((u) => u.is_active !== false).length)}
          helper="Users marked active or unspecified"
          tone="good"
          icon={<CheckCircle2 className="h-4 w-4 text-gray-500" />}
        />
        <KPI
          label="Inactive Users"
          value={formatNumber(users.filter((u) => u.is_active === false).length)}
          helper="Potential reactivation candidates"
          tone={users.some((u) => u.is_active === false) ? "warn" : "neutral"}
          icon={<Clock3 className="h-4 w-4 text-gray-500" />}
        />
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <Panel title="User Role Mix" subtitle="Stakeholder segmentation for admin planning">
          <ProgressList
            rows={stakeholderBreakdown.map((row) => ({
              label: row.role,
              value: row.count,
              display: `${formatNumber(row.count)} (${formatNumber(row.active)} active)`,
              tone: "bg-indigo-600",
            }))}
          />
        </Panel>

        <Panel title="Stakeholder Action Matrix" subtitle="Suggested review cadence by stakeholder category">
          <DataTable
            columns={["Stakeholder Group", "Primary KPIs", "Cadence", "Current Signal", "Recommended Action"]}
            rows={[
              [
                "Executives / Owners",
                "Revenue, order growth, risk, fulfillment reliability",
                "Weekly",
                orders.length > 0 ? "Active data feed" : "Low data volume",
                "Review Executive + AADE tabs",
              ],
              [
                "Sales / Growth",
                "AOV, repeat buyers, top customers, top products",
                "Daily",
                customerOrderStats.repeatCustomers > 0 ? "Retention opportunities visible" : "Need more repeat history",
                "Prioritize growth accounts and repeat-buyer campaigns",
              ],
              [
                "Operations / Fulfillment",
                "Pending queue, backlog aging, status pipeline",
                "Daily",
                backlogAging["8d+"] > 0 ? "Aging risk detected" : "Pipeline stable",
                "Escalate aged orders, clear pending backlog",
              ],
              [
                "Procurement / Supply",
                "Low stock watchlist, demand-linked stockouts",
                "Daily",
                inventoryStats.lowStockCount > 0 ? "Replenishment required" : "Inventory healthy",
                "Restock top-demand low-stock SKUs",
              ],
              [
                "Risk / Compliance / Strategy",
                "AADE anomalies, portfolio health, sync freshness",
                "Weekly / Event-driven",
                aadeAnomalies.length ? `${aadeAnomalies.length} anomaly alerts` : "No active anomaly alerts",
                "Review and acknowledge AADE alerts",
              ],
            ]}
          />
        </Panel>
      </div>

      <Panel title="User Directory Snapshot" subtitle="Tenant-scoped users for stakeholder ownership mapping">
        <DataTable
          columns={["Name", "Email", "Role", "Tenant", "Status"]}
          rows={users.slice(0, 20).map((u) => [
            u.full_name || u.name || "N/A",
            u.email,
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">{u.role || "unknown"}</span>,
            <span className="font-mono text-xs">{(u.tenant_id || "").slice(0, 12) || "-"}</span>,
            u.is_active === false ? (
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">Inactive</span>
            ) : (
              <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">Active</span>
            ),
          ])}
        />
      </Panel>
    </div>
  );

  const renderAADE = () => {
    const healthScore = aadePortfolio?.health_score ?? 0;
    const healthPct = Math.round(healthScore * 100);
    return (
      <div className="space-y-6">
        <div className="rounded-2xl border border-indigo-200 bg-gradient-to-r from-indigo-50 via-white to-blue-50 p-5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="mb-2 inline-flex items-center rounded-full border border-indigo-200 bg-white px-2.5 py-1 text-xs font-semibold text-indigo-700">
                AADE Advanced Analytics (Outsourced)
              </div>
              <h2 className="text-xl font-bold text-gray-900">AI-powered intelligence layer for tenant strategy and risk</h2>
              <p className="mt-1 text-sm text-gray-600">
                AADE handles advanced anomaly detection, portfolio health scoring, profit forecasting, and recommendation workflows.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Link to="/admin/aade/insights" className="rounded-lg border border-indigo-200 bg-white px-3 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-50">
                Open Insights
              </Link>
              <Link to="/admin/aade/dashboard" className="rounded-lg border border-indigo-200 bg-white px-3 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-50">
                AADE Dashboard
              </Link>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
            <div className="rounded-xl border border-indigo-100 bg-white/80 p-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Gateway Health</div>
              <div className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                aadeGatewayHealth?.ok ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
              }`}>
                {aadeGatewayHealth?.overall_status || "unknown"}
              </div>
            </div>
            <div className="rounded-xl border border-indigo-100 bg-white/80 p-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Gateway Mode</div>
              <div className="mt-1 text-sm font-medium text-gray-900">
                {aadeGatewayCapabilities?.gateway_mode || "n/a"}
              </div>
            </div>
            <div className="rounded-xl border border-indigo-100 bg-white/80 p-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Core Upload/Status</div>
              <div className="mt-1 text-sm text-gray-900">
                {aadeGatewayCapabilities?.capabilities?.core_upload && aadeGatewayCapabilities?.capabilities?.core_status_polling ? "enabled" : "disabled"}
              </div>
            </div>
            <div className="rounded-xl border border-indigo-100 bg-white/80 p-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Advanced / Insights</div>
              <div className="mt-1 text-sm text-gray-900">
                {`${aadeGatewayCapabilities?.capabilities?.advanced_analytics ? "adv:on" : "adv:off"} • ${aadeGatewayCapabilities?.capabilities?.insights_pull ? "insights:on" : "insights:off"}`}
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
          <KPI label="Portfolio Health" value={`${healthPct}%`} helper={aadePortfolio ? aadeUiUtils.getRiskScoreLabel(1 - healthScore) : "No portfolio score yet"} tone={healthPct >= 75 ? "good" : healthPct >= 50 ? "warn" : "danger"} icon={<ShieldCheck className="h-4 w-4 text-gray-500" />} />
          <KPI label="AADE Insights" value={formatNumber(aadeInsights.length)} helper="Latest generated insights" icon={<Sparkles className="h-4 w-4 text-gray-500" />} />
          <KPI label="Anomaly Alerts" value={formatNumber(aadeAnomalies.length)} helper="Last 72h detection window" tone={aadeAnomalies.length > 0 ? "warn" : "good"} icon={<AlertTriangle className="h-4 w-4 text-gray-500" />} />
          <KPI label="Sync Jobs" value={formatNumber(aadeSyncJobs.length)} helper="Recent sync history" icon={<RefreshCw className="h-4 w-4 text-gray-500" />} />
          <KPI label="Forecast Outputs" value={formatNumber(aadeForecast?.forecasts?.length || 0)} helper="Profit forecast records in latest response" icon={<LineChart className="h-4 w-4 text-gray-500" />} />
        </div>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
          <Panel title="Portfolio Health & Priority Mix" subtitle="AADE portfolio score + insight severity distribution">
            <div className="mb-5">
              <div className="mb-2 flex items-center justify-between text-sm">
                <span className="text-gray-600">Portfolio Health Score</span>
                <span className="font-semibold text-gray-900">{healthPct}%</span>
              </div>
              <div className="h-3 rounded-full bg-gray-100">
                <div
                  className={`h-3 rounded-full ${
                    healthPct >= 75 ? "bg-emerald-600" : healthPct >= 50 ? "bg-amber-500" : "bg-rose-500"
                  }`}
                  style={{ width: `${clampPercent(healthPct)}%` }}
                />
              </div>
            </div>
            <ProgressList
              rows={aadePriorityBreakdown.map((row) => ({
                label: row.label,
                value: row.value,
                tone:
                  row.label === "critical"
                    ? "bg-rose-500"
                    : row.label === "high"
                    ? "bg-orange-500"
                    : row.label === "medium"
                    ? "bg-amber-500"
                    : "bg-blue-600",
              }))}
            />
          </Panel>

          <Panel title="AADE Sync Status" subtitle="Latest sync pipeline jobs feeding advanced analytics">
            <DataTable
              columns={["Status", "Type", "Records", "Errors", "Started", "Completed"]}
              rows={aadeSyncJobs.slice(0, 8).map((job) => [
                <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                  job.status === "completed"
                    ? "bg-emerald-100 text-emerald-700"
                    : job.status === "failed"
                    ? "bg-rose-100 text-rose-700"
                    : "bg-blue-100 text-blue-700"
                }`}>
                  {job.status}
                </span>,
                job.sync_type.replace(/_/g, " "),
                formatNumber(job.records_synced || 0),
                formatNumber(job.errors_count || 0),
                formatDateTime(job.started_at),
                formatDateTime(job.completed_at),
              ])}
              emptyMessage="AADE sync data not available."
            />
          </Panel>
        </div>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
          <Panel title="Recent AADE Insights" subtitle={`Upload: ${aadeUploadId || "N/A"}`}>
            <DataTable
              columns={["Priority", "Type", "Title", "Confidence", "Actionable", "Generated"]}
              rows={aadeInsights.slice(0, 12).map((insight) => [
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${aadeUiUtils.getInsightPriorityBadgeColor(insight.priority)}`}>
                  {insight.priority}
                </span>,
                insight.type,
                <div>
                  <div className="font-medium text-gray-900">{insight.title}</div>
                  <div className="text-xs text-gray-500">{insight.summary}</div>
                </div>,
                aadeUiUtils.formatConfidence(insight.confidence_score || 0),
                insight.is_actionable ? (
                  <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">Yes</span>
                ) : (
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">No</span>
                ),
                formatDateTime(insight.generated_at),
              ])}
              emptyMessage="No AADE insights returned."
            />
          </Panel>

          <Panel title="Anomaly Alerts & Profit Forecast" subtitle="Risk monitoring and projected outcomes from AADE">
            <div className="space-y-5">
              <div>
                <h4 className="mb-3 text-sm font-semibold text-gray-700">Anomaly Alerts</h4>
                <DataTable
                  columns={["Priority", "Entity", "Title", "Acknowledged", "Generated"]}
                  rows={aadeAnomalies.slice(0, 8).map((alert) => [
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${aadeUiUtils.getInsightPriorityBadgeColor(alert.priority)}`}>
                      {alert.priority}
                    </span>,
                    `${alert.entity_type}:${alert.entity_id?.slice(0, 8)}`,
                    <div>
                      <div className="font-medium">{alert.title}</div>
                      <div className="text-xs text-gray-500">{alert.summary}</div>
                    </div>,
                    alert.acknowledged ? "Yes" : "No",
                    formatDateTime(alert.generated_at),
                  ])}
                  emptyMessage="No anomaly alerts returned."
                />
              </div>

              <div>
                <h4 className="mb-3 text-sm font-semibold text-gray-700">Profit Forecast Outputs</h4>
                <DataTable
                  columns={["Entity", "Confidence", "Generated", "Preview"]}
                  rows={(aadeForecast?.forecasts || []).slice(0, 6).map((f) => [
                    `${f.entity_type}:${String(f.entity_id).slice(0, 10)}`,
                    aadeUiUtils.formatConfidence(f.confidence_score || 0),
                    formatDateTime(f.generated_at),
                    <pre className="max-w-[280px] overflow-x-auto whitespace-pre-wrap text-xs text-gray-600">
                      {JSON.stringify(f.forecast, null, 2).slice(0, 180)}
                      {JSON.stringify(f.forecast, null, 2).length > 180 ? "..." : ""}
                    </pre>,
                  ])}
                  emptyMessage="No forecast outputs returned."
                />
              </div>
            </div>
          </Panel>
        </div>
      </div>
    );
  };

  const renderGovernance = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <KPI label="Tenant Scope" value={tenantContext.hasTenant ? "Active" : "Missing"} helper={tenantContext.tenantId ? tenantContext.tenantId : "Tenant ID not found in local context"} tone={tenantContext.hasTenant ? "good" : "warn"} icon={<ShieldCheck className="h-4 w-4 text-gray-500" />} />
        <KPI label="Data Sources" value="2" helper="AgriFlow CRUD + AADE" icon={<Database className="h-4 w-4 text-gray-500" />} />
        <KPI label="Data Points Loaded" value={formatNumber(totalDataPoints)} helper="Rows/records cached in page state" icon={<BarChart3 className="h-4 w-4 text-gray-500" />} />
        <KPI label="Refresh Time" value={lastRefreshAt ? new Date(lastRefreshAt).toLocaleTimeString() : "N/A"} helper="Last dashboard refresh" icon={<RefreshCw className="h-4 w-4 text-gray-500" />} />
      </div>

      <Panel title="Source Lineage & Ownership" subtitle="Tenant-scoped analytics architecture (CRUD local + AADE advanced)">
        <DataTable
          columns={["Source", "Owner", "Scope", "Coverage", "Last Refresh", "State"]}
          rows={governanceRows.map((row) => [
            row.source,
            row.owner,
            row.scope,
            row.coverage,
            row.freshness === "N/A" ? "N/A" : formatDateTime(row.freshness),
            <span
              className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                row.state === "Healthy"
                  ? "bg-emerald-100 text-emerald-700"
                  : row.state === "Partial"
                  ? "bg-amber-100 text-amber-700"
                  : row.state === "Unavailable"
                  ? "bg-rose-100 text-rose-700"
                  : "bg-gray-100 text-gray-700"
              }`}
            >
              {row.state}
            </span>,
          ])}
        />
      </Panel>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <Panel title="Data Source Health" subtitle="Per-source request status and refresh stamps">
          <DataTable
            columns={["Source", "Status", "Updated", "Error"]}
            rows={Object.entries(sourceStatus).map(([source, status]) => [
              source,
              <span
                className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                  status.state === "ready"
                    ? "bg-emerald-100 text-emerald-700"
                    : status.state === "error"
                    ? "bg-rose-100 text-rose-700"
                    : status.state === "loading"
                    ? "bg-blue-100 text-blue-700"
                    : "bg-gray-100 text-gray-700"
                }`}
              >
                {status.state}
              </span>,
              formatDateTime(status.updatedAt),
              <span className="text-xs text-gray-500">{status.error || "-"}</span>,
            ])}
          />
        </Panel>

        <Panel title="Analytics Contract (Stakeholder-Driven)" subtitle="What stays in AgriFlow vs what moves to AADE">
          <DataTable
            columns={["Analytics Domain", "Primary Consumer", "System of Record", "Notes"]}
            rows={[
              ["CRUD KPIs (orders, products, users)", "Admin / Ops / Sales", "AgriFlow Server", "Fast operational metrics sourced from tenant-scoped CRUD endpoints"],
              ["Portfolio health & scoring", "Executives / Strategy", "AADE", "Computed in AADE from synced operational data"],
              ["Anomaly detection", "Risk / Ops", "AADE", "Event-driven monitoring and alerting"],
              ["Forecasts / recommendations", "Finance / Procurement / Strategy", "AADE", "Advanced models and decision support outputs"],
              ["UI composition and tab navigation", "Stakeholders in Admin UI", "AgriFlow Frontend", "Unified dashboard surface with explicit source badges"],
            ]}
          />
        </Panel>
      </div>
    </div>
  );

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <div className="mb-6 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                Tenant Scoped Analytics
              </span>
              <span className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${sourceBadgeClasses("AgriFlow CRUD")}`}>
                AgriFlow CRUD
              </span>
              <span className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${sourceBadgeClasses("AADE")}`}>
                AADE Advanced
              </span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Stakeholder Analytics Hub</h1>
            <p className="mt-1 text-sm text-gray-600">
              Detailed, tenant-scoped analytics tabs for executives, sales, operations, catalog, and risk. Basic CRUD metrics are sourced from AgriFlow; advanced analytics are outsourced to AADE.
            </p>
          </div>

          <div className="grid gap-2 text-sm text-gray-600">
            <div>
              <span className="font-medium text-gray-800">Tenant:</span>{" "}
              <span className="font-mono text-xs">{tenantContext.tenantId || "Not detected"}</span>
            </div>
            <div>
              <span className="font-medium text-gray-800">User:</span> {tenantContext.userName}
            </div>
            <div>
              <span className="font-medium text-gray-800">Last refresh:</span>{" "}
              {lastRefreshAt ? new Date(lastRefreshAt).toLocaleString() : "Never"}
            </div>
          </div>
        </div>

        <div className="mt-4 flex flex-col gap-3 border-t border-gray-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-2">
            {[7, 30, 90].map((days) => (
              <button
                key={days}
                onClick={() => setTimeRangeDays(days as 7 | 30 | 90)}
                className={`rounded-lg px-3 py-2 text-sm font-medium ${
                  timeRangeDays === days
                    ? "bg-green-700 text-white"
                    : "border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                }`}
              >
                {days}d window
              </button>
            ))}
          </div>
          <button
            onClick={loadData}
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh Analytics
          </button>
        </div>
      </div>

      {!tenantContext.hasTenant && (
        <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Tenant ID is not present in local storage/cookies. Data may fail to load or return global defaults depending on backend auth/session state.
        </div>
      )}

      {(pageError || openSourceErrors.length > 0) && (
        <div className="mb-6 space-y-2">
          {pageError && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">{pageError}</div>
          )}
          {openSourceErrors.map(([key, status]) => (
            <div key={key} className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <span className="font-semibold uppercase">{key}</span>: {status.error || "Unknown error"}
            </div>
          ))}
        </div>
      )}

      <div className="mb-6 overflow-x-auto rounded-2xl border border-gray-200 bg-white p-2 shadow-sm">
        <div className="flex min-w-max gap-2">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const active = tab.key === activeTab;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium transition ${
                  active
                    ? "bg-green-700 text-white shadow"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {activeTab === "overview" && renderOverview()}
      {activeTab === "commercial" && renderCommercial()}
      {activeTab === "operations" && renderOperations()}
      {activeTab === "catalog" && renderCatalog()}
      {activeTab === "stakeholders" && renderStakeholders()}
      {activeTab === "aade" && renderAADE()}
      {activeTab === "governance" && renderGovernance()}
    </div>
  );
};

export default AdminAnalytics;
